/**
 * LanguageSelectorSheet
 *
 * Opens the language picker (`LanguageSelectorContent` — an always-visible,
 * virtualized list of the offered languages) in a sheet, from the home top-bar
 * "More" menu (and elsewhere). Selecting a language updates the saved locale via
 * `LocaleContext` (localStorage `locale`); the sheet stays open so the title
 * below re-renders in the newly chosen language.
 *
 * The shell is deliberately fixed-height and non-scrolling: the body owns its
 * own scroller, so the list scrolls inside the sheet instead of growing it.
 */

import { useLocale } from '@/presentation/contexts/LocaleContext';
import { SheetService } from '../service/SheetService';
import { LanguageSelectorContent } from './components/LanguageSelectorContent';

const LanguageSelectorTitle = () => {
  const { l10n } = useLocale();
  return <>{l10n.settings.language}</>;
};

export interface LanguageSelectorSheetConfig {
  /**
   * Fixed sheet height — **required**. Callers pass
   * `responsive.languageSelectorSheet.fixedHeight`; this module is a plain
   * function, not a component, so it cannot call `useResponsiveDesign()` itself
   * (same pattern as `ChatComposer.tsx:136` and `UserDetailsPage.tsx:187`).
   * It is required rather than optional because the fixed height is a
   * requirement of this sheet, not an option: without it the language list
   * grows the sheet instead of scrolling inside it.
   */
  fixedHeight: string;

  /** Optional container element, screen key string, or null */
  container?: HTMLElement | string | null;

  /** Optional screen key of the route opening this sheet */
  screenKey?: string;

  /** Force the sheet to render as a dialog instead of a bottom sheet */
  forceDialog?: boolean;
}

const show = (config: LanguageSelectorSheetConfig): Promise<void> => {
  return new Promise((resolve) => {
    SheetService.show({
      title: <LanguageSelectorTitle />,
      content: <LanguageSelectorContent />,
      onClose: resolve,
      closeOnClickOutside: true,
      container: config.container,
      screenKey: config.screenKey,
      forceDialog: config.forceDialog,
      fixedHeight: config.fixedHeight,
      // The body owns its own scroller (`LanguageSelectorContent`), so the shell
      // must neither scroll nor intercept wheel/touch on it
      // (`BottomSheetView.tsx:294-299` / `DialogView.tsx:83-87`).
      scrollable: false,
      qaId: 'language-selector',
    });
  });
};

export const LanguageSelectorSheet = {
  show,
};
